<script setup lang="ts">
import type { FormKitFrameworkContext } from '@formkit/core'
import type { TLImageInput, TLImageInputValue } from '~/types/formkit-custom'

type TContext = FormKitFrameworkContext<TLImageInputValue> & TLImageInput

const props = defineProps({
  context: {
    type: Object as PropType<TContext>,
    default: null
  }
})

const fileInputRef = ref<HTMLInputElement | null>(null)
const isSubmitting = ref(false)
const hasUpload = ref(false)
const base64Img = ref<string | null>(null)

function handleFileInput(event: Event) {
  const target = event.target as HTMLInputElement
  const files = target.files
  // Reject empty files
  if (!files) return

  // Save file
  if (files.length === 1 && files[0].type.startsWith('image/')) {
    const reader = new FileReader()
    const file: File = files[0]

    // if image is too large, give an alert
    if (!checkImageSize(file.size)) return

    reader.readAsDataURL(file)
    reader.onload = async () => {
      // upload base64 img
      base64Img.value = reader.result as string
      hasUpload.value = true
      const { width, height } = await getDimensionFromBase64(base64Img.value)

      // upload form value
      props.context.node.input({ file, width, height })
    }
  }
}

const checkImageSize = (size: number) => {
  // For reference
  // 2MB    = 2,097,152 bytes   --> round up 2,100,000
  // 1.5MB  = 1,572,864 bytes   --> round up 1,600,000
  // 1MB    = 1,048,576 bytes   --> round up 1,100,000

  if (size > 2100000) {
    alert(`The file is too big! Please make sure that the size is below 2 MB.`)
    return false
  }
  return true
}

const resetImage = () => {
  hasUpload.value = false
  base64Img.value = null

  // reset form value
  props.context.node.input({
    file: null,
    width: 0,
    height: 0
  })

  // clear up input ref
  const el = fileInputRef.value as HTMLInputElement
  el.value = ''
}

// get dimensions
async function getDimensionFromBase64(base64: string) {
  const img = new Image()
  img.src = base64
  await img.decode()
  return {
    width: img.width,
    height: img.height
  }
}
</script>

<template>
  <div class="image-upload anim-fade-down">
    <div class="image-upload-wrapper clickable">
      <input
        ref="fileInputRef"
        type="file"
        :accept="'image/*'"
        :disabled="isSubmitting"
        @change="handleFileInput"
      >

      <div v-if="!hasUpload" class="image-upload-input" @click="fileInputRef?.click()">
        <CommonIconImage />
        <span>Add image</span>
      </div>

      <div v-else class="image-upload-preview">
        <img v-if="base64Img" class="bg" :src="base64Img" >
        <img v-if="base64Img" class="main" :src="base64Img" >

        <div class="btn">
          <CommonButton primary square @click="resetImage">
            <CommonIconProfileIconDelete />
          </CommonButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$size: 300px;

.image-upload {
  &-wrapper {
    border: 1px solid var(--borderM2);
    border-radius: 7px;
    position: relative;
    overflow: hidden;
    height: $size;
    margin-bottom: 10px;

    input {
      z-index: -1;
    }
  }

  &-input {
    position: absolute;
    background: white;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 20px;
    font-size: 0.95rem;
    color: vaR(--textM2);

    svg {
      height: 40px;
      width: 40px;
      fill: var(--textL2);
    }
  }

  &-preview {
    position: absolute;
    background: white;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    overflow: hidden;

    .main {
      height: $size;
      object-fit: contain;
      width: calc(100% - 130px);
      z-index: 2;
    }

    .bg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      height: 100%;
      width: 100%;
      filter: blur(20px);
      -webkit-filter: blur(20px);
      opacity: 0.2;
      object-fit: cover;
    }

    .btn {
      position: absolute;
      top: 15px;
      right: 15px;
      z-index: 2;
      isolation: isolate;
    }
  }
}

@media (max-width: 750px) {
  .image-upload {
    &-preview {
      .main {
        width: 100%;
      }

      .btn {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 2;
        isolation: isolate;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
      }
    }
  }
}
</style>
